import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConsentManagementPanel = ({ consents, onConsentRevoke, onConsentUpdate }) => {
  const [selectedConsent, setSelectedConsent] = useState(null);
  const [showRevokeModal, setShowRevokeModal] = useState(false);

  const mockConsents = [
    {
      id: 'consent-001',
      organization: 'TechCorp Solutions',
      purpose: 'Employment Verification',
      dataShared: ['Full Name', 'Professional Experience', 'Certifications'],
      grantedDate: '2024-08-15T10:30:00Z',
      expiryDate: '2025-08-15T10:30:00Z',
      status: 'active',
      usageCount: 12,
      lastAccessed: '2024-09-01T14:22:00Z'
    },
    {
      id: 'consent-002',
      organization: 'Global University',
      purpose: 'Academic Verification',
      dataShared: ['Full Name', 'Degree Information', 'Graduation Year'],
      grantedDate: '2024-07-20T09:15:00Z',
      expiryDate: '2025-07-20T09:15:00Z',
      status: 'active',
      usageCount: 8,
      lastAccessed: '2024-08-28T11:45:00Z'
    },
    {
      id: 'consent-003',
      organization: 'HealthCare Provider',
      purpose: 'Medical Credential Access',
      dataShared: ['Full Name', 'Medical License', 'Specialization'],
      grantedDate: '2024-06-10T16:20:00Z',
      expiryDate: '2024-12-10T16:20:00Z',
      status: 'expiring_soon',
      usageCount: 25,
      lastAccessed: '2024-09-02T08:30:00Z'
    },
    {
      id: 'consent-004',
      organization: 'Financial Services Inc',
      purpose: 'Credit Assessment',
      dataShared: ['Full Name', 'Employment Status', 'Income Verification'],
      grantedDate: '2024-05-05T12:00:00Z',
      expiryDate: '2024-11-05T12:00:00Z',
      status: 'limited_usage',
      usageCount: 3,
      lastAccessed: '2024-08-15T13:10:00Z'
    },
    {
      id: 'consent-005',
      organization: 'Government Agency',
      purpose: 'Identity Verification',
      dataShared: ['Full Name', 'Date of Birth', 'Nationality'],
      grantedDate: '2024-03-12T14:45:00Z',
      expiryDate: '2024-09-12T14:45:00Z',
      status: 'expired',
      usageCount: 45,
      lastAccessed: '2024-09-10T16:20:00Z'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'expiring_soon': return 'text-warning';
      case 'limited_usage': return 'text-warning';
      case 'expired': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'CheckCircle';
      case 'expiring_soon': return 'Clock';
      case 'limited_usage': return 'AlertTriangle';
      case 'expired': return 'XCircle';
      default: return 'Circle';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'expiring_soon': return 'Expiring Soon';
      case 'limited_usage': return 'Limited Usage';
      case 'expired': return 'Expired';
      default: return 'Unknown';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRevokeConsent = (consentId) => {
    setSelectedConsent(consentId);
    setShowRevokeModal(true);
  };

  const confirmRevoke = () => {
    if (selectedConsent && onConsentRevoke) {
      onConsentRevoke(selectedConsent);
    }
    setShowRevokeModal(false);
    setSelectedConsent(null);
  };

  const getDaysUntilExpiry = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Consent Management</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor and manage active data sharing agreements with third parties
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Users" size={20} className="text-primary" />
          <span className="text-sm font-medium text-primary">
            {mockConsents?.filter(c => c?.status === 'active')?.length} Active Consents
          </span>
        </div>
      </div>
      <div className="space-y-4">
        {mockConsents?.map((consent) => {
          const daysUntilExpiry = getDaysUntilExpiry(consent?.expiryDate);
          
          return (
            <div key={consent?.id} className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h4 className="font-medium text-foreground">{consent?.organization}</h4>
                    <div className="flex items-center space-x-1">
                      <Icon 
                        name={getStatusIcon(consent?.status)} 
                        size={16} 
                        className={getStatusColor(consent?.status)} 
                      />
                      <span className={`text-xs font-medium ${getStatusColor(consent?.status)}`}>
                        {getStatusLabel(consent?.status)}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-3">{consent?.purpose}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Data Shared:</span>
                      <div className="mt-1">
                        {consent?.dataShared?.map((data, index) => (
                          <span key={index} className="inline-block bg-muted px-2 py-1 rounded text-xs mr-1 mb-1">
                            {data}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div>
                        <span className="text-muted-foreground">Granted:</span>
                        <span className="ml-2 text-foreground">{formatDate(consent?.grantedDate)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Expires:</span>
                        <span className="ml-2 text-foreground">
                          {formatDate(consent?.expiryDate)}
                          {daysUntilExpiry > 0 && daysUntilExpiry <= 30 && (
                            <span className="ml-1 text-warning">({daysUntilExpiry} days)</span>
                          )}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Usage Count:</span>
                        <span className="ml-2 text-foreground">{consent?.usageCount} times</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Last Accessed:</span>
                        <span className="ml-2 text-foreground">{formatDateTime(consent?.lastAccessed)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col space-y-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Eye"
                    iconPosition="left"
                  >
                    View Details
                  </Button>
                  
                  {consent?.status === 'active' && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Settings"
                      iconPosition="left"
                    >
                      Modify
                    </Button>
                  )}
                  
                  <Button
                    variant="destructive"
                    size="sm"
                    iconName="X"
                    iconPosition="left"
                    onClick={() => handleRevokeConsent(consent?.id)}
                  >
                    Revoke
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Revoke Confirmation Modal */}
      {showRevokeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="AlertTriangle" size={24} className="text-warning" />
              <h3 className="text-lg font-semibold text-foreground">Revoke Consent</h3>
            </div>
            
            <p className="text-sm text-muted-foreground mb-6">
              Are you sure you want to revoke this consent? The organization will no longer be able to access your credentials, and this action cannot be undone.
            </p>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowRevokeModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmRevoke}
                className="flex-1"
              >
                Revoke Consent
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="mt-6 p-4 bg-muted/30 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={16} className="text-primary mt-0.5" />
          <div className="text-sm">
            <p className="text-foreground font-medium">Consent Tracking</p>
            <p className="text-muted-foreground mt-1">
              All data sharing activities are logged and tracked. You can revoke consent at any time, 
              and organizations will be immediately notified of the change.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentManagementPanel;